import { ChevronDown, ChevronLeft, ChevronUp, Search } from "lucide-react";
import { DndContext, rectIntersection, DragOverlay } from "@dnd-kit/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	Box,
	Button,
	Autocomplete,
	TextField,
	Collapse,
	IconButton,
} from "@mui/material";

import { DropdownFilter } from "../../widgets/DropdownFilter/DropdownFilter";
import hospitalsServices from "../../services/hospitalsServices";
import categoryServices from "../../services/categoryServices";
import KanbanLane from "../../widgets/KanbanLane/KanbanLane";
import KanbanCard from "../../widgets/KanbanCard/KanbanCard";
import { ILoggedUser } from "../../store/actions";
import { Position } from "../Login/LoginUtils";
import useToken from "../../utils/useToken";
import { URI_API } from "../../utils/api";
import KanbanIdea from "./KanbanIdea";
import "./Kanban.scss";

export default function Kanban() {
	const loggedUser: ILoggedUser = useSelector(
		(state: any) => state.loggedUser.loggedUser,
	);

	const navigator = useNavigate();
	const tokenController = useToken();

	useEffect(() => {
		setIsLoading(true);
		fetch(`${URI_API}/idea/all`, {
			headers: {
				Authorization: `Bearer ${tokenController.getToken()}`,
			},
		})
			.then((res) => res.json())
			.then((ideasFromApi) => {
				const ideas = ideasFromApi.map((idea: any) => {
					const { hospital_areas = [] } = idea;
					const { hospital_units = [] } = idea;

					let hospitalAreas = "";
					if (hospital_areas.length > 0 && hospital_units.length > 0) {
						const first = hospital_areas[0];
						const firstUnit = hospital_units[0];
						hospitalAreas = `${first.area_name} • ${firstUnit.name}`;

						const extra = hospital_areas.length - 1;
						if (extra > 0) hospitalAreas += ` + ${extra}`;
					}

					return { ...idea, hospitalAreas };
				});

				setAllIdeas(ideas);

				const titles = ideas.map((idea: any) => idea.title);

				setIdeaTitles(titles);
				setIsLoading(false);
			});
	}, []);

	const [isLoading, setIsLoading] = useState(false);
	const [allIdeas, setAllIdeas] = useState<Array<any>>([]);
	const [visibleIdeas, setVisibleIdeas] = useState<Array<any>>([]);
	const [ideaTitles, setIdeaTitles] = useState<Array<any>>([]);
	const [show, setShow] = useState(true);
	const [selectedUnits, setSelectedUnits] = useState<any[]>([]);
	const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
	const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
	const [selectedStartDate, setSelectedStartDate] = useState<Date>();
	const [selectedFinishDate, setSelectedFinishDate] = useState<Date>();
	const [searchInput, setSearchInput] = useState<string>();
	const [hospitalAreaOptions, setHospitalAreaOptions] = useState<any[]>([]);
	const [areaOptions, setAreaOptions] = useState<any[]>([]);
	const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
	const [activeCard, setActiveCard] = useState<any | null>(null);
	const [selectedIdea, setSelectedIdea] = useState<any | null>(null);
	const [areActiveFilters, setAreActiveFilters] = useState(false);

	useEffect(() => {
		const hasFilters =
			selectedAreas.length > 0 ||
			selectedCategories.length > 0 ||
			selectedUnits.length > 0 ||
			!!selectedStartDate ||
			!!selectedFinishDate;

		setAreActiveFilters(hasFilters);
	}, [
		selectedAreas,
		selectedCategories,
		selectedUnits,
		selectedStartDate,
		selectedFinishDate,
	]);

	const todoItems = useMemo(
		() => visibleIdeas.filter((item) => item.status_id === 1),
		[visibleIdeas],
	);
	const inProgressItems = useMemo(
		() => visibleIdeas.filter((item) => item.status_id === 2),
		[visibleIdeas],
	);
	const doneItems = useMemo(
		() => visibleIdeas.filter((item) => item.status_id === 3),
		[visibleIdeas],
	);
	const uItems = useMemo(
		() => visibleIdeas.filter((item) => item.status_id === 4),
		[visibleIdeas],
	);
	const inImplementationItems = useMemo(
		() => visibleIdeas.filter((item) => item.status_id === 5),
		[visibleIdeas],
	);
	const backlogItems = useMemo(
		() => visibleIdeas.filter((item) => item.status_id === 6),
		[visibleIdeas],
	);

	const [menuState, setMenuState] = useState<{
		id: string | null;
		anchorEl: HTMLElement | null;
	}>({ id: null, anchorEl: null });

	const updateStatus = (ideaId: number, statusId: number, creatorId: number) => {
		fetch(`${URI_API}/idea/update_status/${ideaId}/${statusId}/${creatorId}`, {
			method: "PUT",
			headers: {
				Authorization: `Bearer ${tokenController.getToken()}`,
			},
		})
			.then((res) => res.json())
			.then(() => console.log("succefully updated"));
	};

	const handleOpen = (id: string) => (e: React.MouseEvent<HTMLButtonElement>) =>
		setMenuState({ id, anchorEl: e.currentTarget });

	const handleClose = () => setMenuState({ id: null, anchorEl: null });

	useEffect(() => {
		const filtered = allIdeas.filter((idea) => {
			const matchesTitle =
				!searchInput?.trim() ||
				idea.title.toLowerCase().includes(searchInput.toLowerCase());

			const matchesUnits =
				selectedUnits.length === 0 ||
				selectedUnits.some((unit) =>
					idea.hospital_units.some((h: any) => h.id === unit.value),
				);

			const matchesAreas =
				selectedAreas.length === 0 ||
				selectedAreas.some((area) =>
					idea.hospital_areas.some(
						(a: any) => a.hospital_area_id === area.value,
					),
				);

			const matchesCategories =
				selectedCategories.length === 0 ||
				selectedCategories.some(
					(category) => category.value === idea.category_id,
				);

			const ideaDate = new Date(idea.created_at);
			const matchesStartDate = !selectedStartDate || ideaDate >= selectedStartDate;

			const matchesFinishDate =
				!selectedFinishDate || ideaDate <= selectedFinishDate;

			return (
				matchesTitle &&
				matchesUnits &&
				matchesAreas &&
				matchesCategories &&
				matchesStartDate &&
				matchesFinishDate
			);
		});
		setVisibleIdeas(filtered);
	}, [
		searchInput,
		selectedUnits,
		selectedAreas,
		selectedCategories,
		selectedStartDate,
		selectedFinishDate,
		allIdeas,
	]);

	useEffect(() => {
		getAllActiveHospitals();
		getAllAreas();
		getAllCategories();
	}, []);

	const getAllActiveHospitals = () => {
		hospitalsServices.getAllActiveHospitals().then((hospitalAreas) => {
			let aux = [];
			for (let hospitalArea of hospitalAreas) {
				aux.push({
					label: hospitalArea["hospital_name"],
					value: hospitalArea["id"],
				});
			}
			setHospitalAreaOptions(aux);
		});
	};

	const getAllAreas = () => {
		hospitalsServices.getAllActiveAreas().then((areas) => {
			const aux = areas.map((a: any) => ({
				label: a.area_name,
				value: a.id,
			}));
			setAreaOptions(aux);
		});
	};

	const getAllCategories = () => {
		categoryServices.getAllActiveCategories().then((categories) => {
			let aux = [];
			for (let category of categories) {
				aux.push({
					label: category["category"],
					value: category["id"],
				});
			}
			setCategoryOptions(aux);
		});
	};

	const getStatusFromContainer = (container: any) => {
		switch (container) {
			case "IDEA":
				return 1;
			case "EN REVISIÓN":
				return 3;
			case "DESARROLLO":
				return 2;
			case "IMPLEMENTACIÓN":
				return 5;
			case "FINALIZADO":
				return 4;
			case "BACKLOG":
				return 6;
			default:
				return null;
		}
	};

	const handleCardClick = (id: number) => {
		const foundIdea = visibleIdeas.find((idea) => idea.idea_id === id);
		if (foundIdea) {
			setSelectedIdea(foundIdea);
		}
	};

	const clearAll = () => {
		setSelectedUnits([]);
		setSelectedAreas([]);
		setSelectedCategories([]);
		setSelectedStartDate(undefined);
		setSelectedFinishDate(undefined);
	};

	if (loggedUser.position !== Position.COLAB)
		return (
			<Box>
				{selectedIdea === null ? (
					<Box className="backbackground">
						<Box className="general-container">
							<Box className="header">
								<Button
									className="header__go-back"
									onClick={() => navigator(-1)}
								>
									<ChevronLeft className="go-back__icon" />
									Regresar
								</Button>
								<Box className="header__searching">
									<Box className="header__searching__search-bar">
										<Box>
											<Search className="header__searching__search-bar__search-icon" />
										</Box>
										<Autocomplete
											freeSolo
											forcePopupIcon={false}
											fullWidth
											options={ideaTitles}
											value={searchInput}
											inputValue={searchInput ?? ""}
											onChange={(e, newVal) => {
												setSearchInput(newVal);
											}}
											onInputChange={(e, newInput, reason) => {
												if (reason === "clear") {
													setSearchInput("");
												} else {
													setSearchInput(newInput);
												}
											}}
											renderInput={(params) => (
												<TextField
													value={searchInput}
													placeholder={"Buscar"}
													{...params}
												/>
											)}
										/>
										<IconButton
											onClick={() => {
												if (show === false) {
													setShow(true);
												} else {
													setShow(false);
												}
											}}
											disableRipple
										>
											{show ? (
												<ChevronUp className="header__searching__search-bar__collapse-icon" />
											) : (
												<ChevronDown className="header__searching__search-bar__collapse-icon" />
											)}
										</IconButton>
									</Box>
									<Collapse in={show}>
										<Box className="header__searching__filters">
											<Box className="header__searching__filters__dropdowns">
												<DropdownFilter
													id="unit"
													type="options"
													label="Unidad"
													options={hospitalAreaOptions}
													selected={selectedUnits}
													onChange={setSelectedUnits}
													anchorEl={menuState.anchorEl}
													open={menuState.id === "unit"}
													onOpen={handleOpen}
													onClose={handleClose}
												/>
												<DropdownFilter
													id="area"
													type="options"
													label="Área"
													options={areaOptions}
													selected={selectedAreas}
													onChange={setSelectedAreas}
													anchorEl={menuState.anchorEl}
													open={menuState.id === "area"}
													onOpen={handleOpen}
													onClose={handleClose}
												/>
												<DropdownFilter
													id="category"
													type="options"
													label="Categoría"
													options={categoryOptions}
													selected={selectedCategories}
													onChange={setSelectedCategories}
													anchorEl={menuState.anchorEl}
													open={menuState.id === "category"}
													onOpen={handleOpen}
													onClose={handleClose}
												/>
												<DropdownFilter
													id="start"
													type="date"
													label="Fecha de inicio"
													selectedDate={selectedStartDate}
													onDateChange={setSelectedStartDate}
													anchorEl={menuState.anchorEl}
													open={menuState.id === "start"}
													onOpen={handleOpen}
													onClose={handleClose}
												/>
												<DropdownFilter
													id="end"
													type="date"
													label="Fecha de fin"
													selectedDate={selectedFinishDate}
													onDateChange={setSelectedFinishDate}
													anchorEl={menuState.anchorEl}
													open={menuState.id === "end"}
													onOpen={handleOpen}
													onClose={handleClose}
												/>
											</Box>
											<Box className="header__searching__filters__clear">
												{areActiveFilters && (
													<Button onClick={clearAll}>
														Limpiar Todo
													</Button>
												)}
											</Box>
										</Box>
									</Collapse>
								</Box>
							</Box>

							<DndContext
								onDragStart={(e) => {
									setActiveCard({ ...e.active.data.current });
								}}
								collisionDetection={rectIntersection}
								onDragEnd={(e) => {
									setActiveCard(null);
									const container = e.over?.id;
									const dragged = e.active.data.current;

									if (
										!container ||
										!dragged ||
										container === dragged.parent
									)
										return;

									const newStatus = getStatusFromContainer(container);
									if (!newStatus) return;

									updateStatus(
										dragged.idea_id,
										newStatus,
										loggedUser.id,
									);

									setVisibleIdeas((prev) =>
										prev.map((item) =>
											item.idea_id === dragged.idea_id
												? { ...item, status_id: newStatus }
												: item,
										),
									);
								}}
							>
								<Box className="columns">
									<KanbanLane
										isLoading={isLoading}
										headerColor="#0CB3EB"
										title="IDEA"
										items={todoItems}
										activeCard={activeCard}
										onCardClick={handleCardClick}
									/>
									<KanbanLane
										isLoading={isLoading}
										headerColor="#97D700"
										title="EN REVISIÓN"
										items={doneItems}
										activeCard={activeCard}
										onCardClick={handleCardClick}
									/>
									<KanbanLane
										isLoading={isLoading}
										headerColor="#EBAC00"
										title="DESARROLLO"
										items={inProgressItems}
										activeCard={activeCard}
										onCardClick={handleCardClick}
									/>
									<KanbanLane
										isLoading={isLoading}
										headerColor="#066964"
										title="IMPLEMENTACIÓN"
										items={inImplementationItems}
										activeCard={activeCard}
										onCardClick={handleCardClick}
									/>
									<KanbanLane
										isLoading={isLoading}
										headerColor="#671E75"
										title="FINALIZADO"
										items={uItems}
										activeCard={activeCard}
										onCardClick={handleCardClick}
									/>
									<KanbanLane
										isLoading={isLoading}
										headerColor="#BEBEBE"
										title="BACKLOG"
										items={backlogItems}
										activeCard={activeCard}
										onCardClick={handleCardClick}
									/>
								</Box>

								<DragOverlay
									style={{
										zIndex: 99999,
										pointerEvents: "none",
										cursor: "grabbing !important",
									}}
								>
									{activeCard ? (
										<KanbanCard
											area={activeCard.hospitalAreas}
											authors={activeCard.authors}
											ideaDate={activeCard.created_at}
											idea_id={activeCard.idea_id}
											title={activeCard.title}
											index={activeCard.index}
											key={activeCard.idea_id}
											parent={activeCard.parent}
										/>
									) : null}
								</DragOverlay>
							</DndContext>
						</Box>
					</Box>
				) : (
					<KanbanIdea
						idea={selectedIdea}
						back={() => setSelectedIdea(null)}
					/>
				)}
			</Box>
		);
	else {
		navigator("/");
		return null;
	}
}
